import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { THEMES, isThemeKey, isThemeMode, type ThemeKey, type ThemeMode, type ResolvedMode } from '../styles/themes';
import { reapplyCustomThemeForMode } from '../lib/theme-custom';

interface ThemeState {
  theme: ThemeKey;
  mode: ThemeMode;
  setTheme: (theme: ThemeKey) => void;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const PREFERS_DARK = '(prefers-color-scheme: dark)';

/**
 * mode='auto' 면 OS prefers-color-scheme 로 light|dark resolve(§17.2/§16-C7).
 * data-mode 에는 절대 'auto' 가 들어가지 않는다(tokens.css 에 auto 셀렉터 없음).
 */
function resolveMode(mode: ThemeMode): ResolvedMode {
  if (mode === 'auto') {
    if (typeof window === 'undefined' || !window.matchMedia) return 'light';
    return window.matchMedia(PREFERS_DARK).matches ? 'dark' : 'light';
  }
  return mode;
}

/**
 * ★★ 모드를 바꾼 뒤 **회사·개인 테마를 다시 입힌다** (2026-08-29).
 *    그 테마의 «밝기 계열»(배경·표면·글자·테두리)은 밝은 모드에서만 얹는데, 그 판정이
 *    `data-mode` 를 보고 갈리기 때문이다. 다시 안 입히면 어둡게로 바꿔도 밝은 모드의
 *    글자색이 인라인으로 남아 **명암비 1.14** 가 된다(운영에서 실제로 그랬다).
 */
function applyToDocument(theme: ThemeKey, mode: ThemeMode) {
  if (typeof document === 'undefined') return;
  const resolved = resolveMode(mode);
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.setAttribute('data-mode', resolved);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? THEMES[theme].primaryDark : THEMES[theme].primaryLight);
  reapplyCustomThemeForMode();
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'blue',
      mode: 'light',
      setTheme: (theme) => {
        if (!isThemeKey(theme)) return;
        applyToDocument(theme, get().mode);
        set({ theme });
      },
      setMode: (mode) => {
        if (!isThemeMode(mode)) return;
        applyToDocument(get().theme, mode);
        set({ mode });
      },
      toggleMode: () => {
        // 명시 토글은 auto 를 벗어나 light↔dark 로 고정(현재 화면 모드의 반대로).
        const next: ThemeMode = resolveMode(get().mode) === 'light' ? 'dark' : 'light';
        applyToDocument(get().theme, next);
        set({ mode: next });
      },
    }),
    {
      name: 'workchat:theme',
      onRehydrateStorage: () => (state) => {
        // 캐시에 'auto' 가 들어있어도 applyToDocument 가 resolve 하므로 data-mode 는 light|dark.
        if (state) applyToDocument(state.theme, state.mode);
      },
    },
  ),
);

/**
 * OS 모드 변경 추종 — 모듈 레벨 단일 matchMedia 핸들(§16-I4 누수 차단).
 * 재등록 시 직전 리스너를 해제하므로 idempotent. store mode 가 'auto' 일 때만 재적용.
 */
let autoModeMql: MediaQueryList | null = null;
let autoModeListener: ((e: MediaQueryListEvent) => void) | null = null;

function ensureAutoModeListener() {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  // 직전 핸들 해제(재등록 idempotent)
  if (autoModeMql && autoModeListener) autoModeMql.removeEventListener('change', autoModeListener);
  autoModeMql = window.matchMedia(PREFERS_DARK);
  autoModeListener = () => {
    const { theme, mode } = useThemeStore.getState();
    if (mode === 'auto') applyToDocument(theme, 'auto');
  };
  autoModeMql.addEventListener('change', autoModeListener);
}

ensureAutoModeListener();
